import { JsonSchema } from '../../utils'
import * as LatestReadingFormsApiResponse from './readings-forms-api-response.model'

type ReadingFormsApiResponse = {
  manualReadingBatchForm: Form[]
  distributionTree: DistributionTree[]
}

export type T = ReadingFormsApiResponse

type Form = {
  id: string
  version: number
  uuid:	string
  name: string
  scopeName: string
  scopeUuid:	string
  startDate: string
  startInterval: string
  endInterval: string
  endDate?: string
  periodicityLabel: string
  periodicityCode: string
  devices: Device[]
}

type Device = {
  id: string
  designation: string
  nomenclature?: string
  local?: string
  position: number
  distributionNodes: {
    distributionTreeId: string
    nodeId: string
  }[]
  readings: ReadingForm[]
  validationRules: Rule[]
  datetimeInputConfig: DatetimeInputConfig
}

type ReadingForm = {
  measureId: string
  signalUnit: string
  signalUnitSymbol: string
  signalQuantity: string
  signalKey: string
  position: number
  signalAbbreviation:	string
  signalQuantitySymbol:	string
  validationRules: Rule[]
  infoConfig: {
    type: 'index' | 'consumption' | 'hhv'
  },
  inputConfig: {
    type: 'default' | 'last-value-by-default'
  }
}

type Rule = {
  type: string, level: 'error' | 'warn', params?: Record<string, unknown>
}

type DatetimeInputConfig = DatetimeConfig
| DateRangeConfig
| DatetimeRangeConfig

type DatetimeConfig = {
  type:	'datetime'
  time:	{
    isRequired: boolean
    resolution: string
  }
}
type DateRangeConfig = {
  type:	'date-range'
}
type DatetimeRangeConfig = {
  type:	'datetime-range'
  time:	{
    isRequired: boolean
    resolution: string
  }
}
const DATE_TIME_CONFIG_SCHEMA: JsonSchema.T<DatetimeConfig> = {
  type      : 'object',
  required  : [ 'type', 'time' ],
  properties: {
    type: { type: 'string', 'const': 'datetime' },
    time: {
      type      : 'object',
      required  : [ 'isRequired', 'resolution' ],
      properties: {
        isRequired: { type: 'boolean' },
        resolution: { type: 'string' },
      }
    }
  }
}
const DATE_RANGE_CONFIG_SCHEMA: JsonSchema.T<DateRangeConfig> = {
  type      : 'object',
  required  : [ 'type' ],
  properties: { type: { type: 'string', 'const': 'date-range' } }
}
const DATE_TIMERANGE_CONFIG_SCHEMA: JsonSchema.T<DatetimeRangeConfig> = {
  type      : 'object',
  required  : [ 'type', 'time' ],
  properties: {
    type: { type: 'string', 'const': 'datetime-range' },
    time: {
      type      : 'object',
      required  : [ 'isRequired', 'resolution' ],
      properties: {
        isRequired: { type: 'boolean' },
        resolution: { type: 'string' },
      }
    }
  }
}
const DATETIME_INPUT_CONFIG_SCHEMA: JsonSchema.T<DatetimeInputConfig> = {
  anyOf: [
    DATE_TIME_CONFIG_SCHEMA,
    DATE_RANGE_CONFIG_SCHEMA,
    DATE_TIMERANGE_CONFIG_SCHEMA,
  ]
}
const RULE_SCHEMA: JsonSchema.T<Rule> = {
  type    : 'object',
  required: [
    'type',
    'level',
  ],
  properties: {
    type  : { type: 'string' },
    level : { type: 'string', 'enum': [ 'error', 'warn' ] },
    params: { type: 'object', nullable: true },
  }
}
const READING_FORM_SCHEMA: JsonSchema.T<ReadingForm> = {
  type    : 'object',
  required: [
    'measureId',
    'signalUnit',
    'signalUnitSymbol',
    'signalQuantity',
    'signalKey',
    'position',
    'signalAbbreviation',
    'signalQuantitySymbol',
    'validationRules',
    'infoConfig',
    'inputConfig',
  ],
  properties: {

    measureId           : { type: 'string' },
    signalUnit          : { type: 'string' },
    signalUnitSymbol    : { type: 'string' },
    signalQuantity      : { type: 'string' },
    signalKey           : { type: 'string' },
    position            : { type: 'number' },
    signalAbbreviation  : { type:	'string' },
    signalQuantitySymbol: { type:	'string' },
    validationRules     : {
      type : 'array',
      items: RULE_SCHEMA
    },
    infoConfig: {
      type      : 'object',
      required  : [ 'type' ],
      properties: { type: { type: 'string', 'enum': [ 'index', 'consumption', 'hhv' ] } }
    },
    inputConfig: {
      type      : 'object',
      required  : [ 'type' ],
      properties: { type: { type: 'string', 'enum': [ 'default', 'last-value-by-default' ] } }
    }
  }
}
const DEVICE_SCHEMA: JsonSchema.T<Device> = {
  type    : 'object',
  required: [
    'id',
    'designation',
    'position',
    'distributionNodes',
    'readings',
    'validationRules',
    'datetimeInputConfig',
  ],
  properties: {
    id               : { type: 'string' },
    designation      : { type: 'string' },
    nomenclature     : { type: 'string', nullable: true },
    local            : { type: 'string', nullable: true },
    position         : { type: 'number' },
    distributionNodes: {
      type : 'array',
      items: {
        type      : 'object',
        required  : [ 'distributionTreeId', 'nodeId' ],
        properties: {
          distributionTreeId: { type: 'string' },
          nodeId            : { type: 'string' },
        }
      }
    },
    readings: {
      type : 'array',
      items: READING_FORM_SCHEMA
    },
    validationRules: {
      type : 'array',
      items: RULE_SCHEMA
    },
    datetimeInputConfig: DATETIME_INPUT_CONFIG_SCHEMA
  }
}
const FORM_SCHEMA: JsonSchema.T<Form> = {
  type    : 'object',
  required: [
    'id',
    'version',
    'uuid',
    'name',
    'scopeName',
    'scopeUuid',
    'startDate',
    'startInterval',
    'endInterval',
    'periodicityLabel',
    'periodicityCode',
    'devices'
  ],
  properties: {
    id              : { type: 'string' },
    version         : { type: [ 'number' ] },
    uuid            :	{ type: 'string' },
    name            : { type: 'string' },
    scopeName       : { type: 'string' },
    scopeUuid       :	{ type: 'string' },
    startDate       : { type: 'string' },
    startInterval   : { type: 'string' },
    endInterval     : { type: 'string' },
    endDate         : { type: 'string', nullable: true },
    periodicityLabel: { type: 'string' },
    periodicityCode : { type: 'string' },
    devices         : {
      type : 'array',
      items: DEVICE_SCHEMA
    }
  }
}

const DISTRIBUTION_NODE_SCHEMA:JsonSchema.T<DistributionNode> = {
  type    : 'object',
  required: [],
  anyOf   : [
    {
      type      : 'object',
      required  : [ 'id', 'parent' ],
      properties: {
        id    : { type: 'string' },
        parent: { type: 'string' },
      }
    },
    {
      type      : 'object',
      required  : [ 'id', 'parent' ],
      properties: {
        id    : { type: 'string' },
        parent: { type: 'null' },
      }
    }
  ]
}
const DISTRIBUTION_TREE_SCHEMA:JsonSchema.T<DistributionTree> = {
  type      : 'object',
  required  : [ 'id', 'nodes' ],
  properties: {
    id   : { type: 'string' },
    nodes: {
      type : 'array',
      items: DISTRIBUTION_NODE_SCHEMA
    }
  }
}
const READING_FORMS_API_RESPONSE_SCHEMA:JsonSchema.T<ReadingFormsApiResponse> = {
  type      : 'object',
  required  : [ 'manualReadingBatchForm', 'distributionTree' ],
  properties: {
    manualReadingBatchForm: {
      type : 'array',
      items: FORM_SCHEMA
    },
    distributionTree: {
      type : 'array',
      items: DISTRIBUTION_TREE_SCHEMA
    }
  }
}
type DistributionNode = {
  id: string
  parent: string
} | {
  id: string
  parent: null
}

type DistributionTree = {
  id: string
  nodes: DistributionNode[]
}

export const parse = JsonSchema.parserFor(READING_FORMS_API_RESPONSE_SCHEMA)

const deviceToLatest: (startOfOperation:string, endOfOperation?:string)=>(device: Device) => LatestReadingFormsApiResponse.Device
  = (startOfOperation:string, endOfOperation?:string) =>
    ({ id, designation, nomenclature, local, position, datetimeInputConfig, distributionNodes, readings, validationRules }) => ({
      id,
      designation,
      ...((nomenclature !== undefined) ? { nomenclature } : {}),
      ...((local !== undefined) ? { location: local } : {}),
      position,
      datetimeInputConfig,
      distributionNodes,
      readings,
      validationRules,
      startOfOperation,
      ...((endOfOperation !== undefined) ? { endOfOperation } : {}),
    })

const dateStringToIso8601 = (dateString:string)=> (new Date(dateString)).toISOString()

const formToLatest: (form: Form) => LatestReadingFormsApiResponse.Form
  = ({ id, name, periodicityCode, startDate, endDate, startInterval, endInterval, devices })=>({
    id,
    name,
    periodicityCode,
    startInterval,
    endInterval,
    devices: devices.map(deviceToLatest(
      dateStringToIso8601(startDate), 
      (endDate !== undefined) ? dateStringToIso8601(startDate) : undefined
    ))
  })

export const toLatest: (readingFormsApiResponse: ReadingFormsApiResponse )=> LatestReadingFormsApiResponse.T
  = ({ distributionTree, manualReadingBatchForm })=>({
    distributionTree,
    manualReadingBatchForm: manualReadingBatchForm.map(formToLatest)
  })
